import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Loader2 } from "lucide-react";
import ItemCombobox from "./ItemCombobox";

export default function TransferInventoryDialog({ open, onOpenChange, project, onSuccess }) {
  const [items, setItems] = useState([]);
  const [itemId, setItemId] = useState("");
  const [qty, setQty] = useState(1);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setItemId("");
      setQty(1);
      setNote("");
      api.get("/inventory/items").then((r) => setItems(r.data));
    }
  }, [open]);

  const selected = items.find((i) => i.id === itemId);
  const submit = async (e) => {
    e.preventDefault();
    if (!itemId) return toast.error("Select an item");
    setBusy(true);
    try {
      await api.post("/inventory/transfer", { item_id: itemId, project_id: project.id, quantity: Number(qty), note });
      toast.success("Inventory transferred to project");
      onOpenChange(false);
      setItemId(""); setQty(1); setNote("");
      onSuccess?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Transfer failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-md" data-testid="transfer-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black tracking-tight">Transfer to Project</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Item</Label>
            <div className="mt-1.5">
              <ItemCombobox
                items={items}
                value={itemId}
                onChange={setItemId}
                testId="transfer-item"
                placeholder="Type SKU or name…"
              />
            </div>
            {selected && (
              <div className="mt-1 text-xs text-slate-500 font-mono">
                In stock: <span className="font-semibold text-slate-900">{selected.quantity}</span> {selected.unit}
              </div>
            )}
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Quantity</Label>
            <Input type="number" min={1} value={qty} onChange={(e) => setQty(e.target.value)} data-testid="transfer-qty-input" className="rounded-sm mt-1.5" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Note</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="transfer-note-input" className="rounded-sm mt-1.5" rows={2} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="transfer-submit-btn" className="bg-blue-800 hover:bg-blue-900 text-white rounded-sm">
              {busy && <Loader2 size={16} className="animate-spin mr-1.5" />}
              Transfer
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
